import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import React, { createContext, useContext, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { setUserAction, setUserDataAction } from "../loginReducer";
import { SmallLogoWithDashboard } from "../logo/SmallLogo";
import LogoutWidget from "../logout/LogoutWidget";
import { UserData } from "../model/user";
import { ApplicationState } from "../reducers";
import { deleteMode, getModeUsage, ModeStatistics, setMode } from "../services/adminService";
import NotificationOverlay from "../status/NotificationOverlay";
import { Grid } from "../layout/Row";
import ActionButton from "../button/ActionButton";
import { getOperatingModes } from "../services/organisationService";

const Header = (props: Props) => {
  return (
    <>
      <div className={"Dashboard-header"}>
        <SmallLogoWithDashboard fontSizeRem={1.625} />
        <LogoutWidget {...props} />
      </div>
      <NotificationOverlay />
    </>
  );
};

const Details = ({ mode, jwtToken, availableModes }: { mode: ModeStatistics; jwtToken: string; availableModes: string[] }) => {
  const [loading, setLoading] = React.useState(false);
  const refresh = useApiTrigger();
  const margin = { margin: "0.25rem 0" };
  if (!mode.organizations.length) {
    return (
      <>
        <h4 style={{ ...margin, opacity: 0.5 }}>Not in use</h4>
        <ActionButton
          text="DELETE"
          onClick={() => {
            setLoading(true);
            deleteMode(jwtToken, mode.name).then(() => {
              refresh();
              setLoading(false);
            });
          }}
          style={{ marginLeft: 0, opacity: loading ? 0.5 : 1 }}
        />
      </>
    );
  }
  return (
    <>
      <h4 style={margin}>Used by</h4>
      {mode.organizations.map((organisation) => (
        <Organisation key={organisation.id} org={organisation} availableModes={availableModes} selectedMode={mode.name} jwtToken={jwtToken} />
      ))}
      {!mode.exists &&
        <div>
          <h4 style={{ color: "red" }}>Mode does not exist</h4>
          <ActionButton
            text="SET ALL TO DEFAULT"
            onClick={async () => {
              setLoading(true);
              try {
                await Promise.all(
                  mode.organizations.map((organisation) =>
                    setMode(jwtToken, Number(organisation.id), "DEFAULT").catch(() => {
                      alert(`Failed to set mode for ${organisation.name}`);
                    })
                  )
                );
                refresh();
              } finally {
                setLoading(false);
              }
            }}
            style={{ marginLeft: 0, opacity: loading ? 0.5 : 1 }}
          />
        </div>
      }
    </>
  );
};

const ApiTriggerContext = createContext(() => { });
export const useApiTrigger = () => useContext(ApiTriggerContext);

const Mode = ({ mode, jwtToken, availableModes }: { mode: ModeStatistics; jwtToken: string; availableModes: string[] }) => {
  if (!mode.organizations) {
    return null;
  } else {
  }
  return (
    <div className={"Widget-item"}>
      <h3>{mode.name}</h3>
      <Details mode={mode} jwtToken={jwtToken} availableModes={availableModes} />
    </div>
  );
};

const Modes = ({ modes, jwtToken }: { modes: ModeStatistics[]; jwtToken: string }) => {
  const [availableModes, setAvailableModes] = React.useState<string[]>([]);
  React.useEffect(() => {
    if (jwtToken) {
      getOperatingModes(jwtToken)
        .then((modes) => {
          setAvailableModes(modes);
        })
        .catch(() => {
          console.error("Failed to fetch available modes");
        });
    }
  });
  return (
    <Grid>
      {modes.map((mode, index) => (
        <Mode key={index} mode={mode} jwtToken={jwtToken} availableModes={availableModes} />
      ))}
    </Grid>
  );
};

const ModePage = (props: Props) => {
  const [modes, setModes] = React.useState<ModeStatistics[]>([]);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const refresh = () => {
    if (props.jwtToken) {
      getModeUsage(props.jwtToken)
        .then((modes) => {
          setModes(modes);
        })
        .catch((response) => {
          setError("Couldn't get mode usage");
        });
    }
  };
  useEffect(() => {
    refresh();
  }, [props.jwtToken]);
  return (
    <div className="Dashboard">
      <Header {...props} />
      {props.jwtToken ? (
        <ApiTriggerContext.Provider value={refresh}>
          <Modes modes={modes} jwtToken={props.jwtToken} />
        </ApiTriggerContext.Provider>
      ) : (
        <div />
      )}
    </div>
  );
};

const Organisation = ({ org, availableModes, selectedMode, jwtToken }: { org: { id: String, name: String }, availableModes: string[]; selectedMode: string; jwtToken: string }) => {
  const [active, setActive] = React.useState<Boolean>(false);
  const margin = { margin: "0.25rem 0" };
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <p style={margin}>{org.name}</p>
        <ActionButton
          text="CHANGE"
          onClick={() => setActive(!active)}
          style={{ marginLeft: 0, fontSize: "0.75rem" }}
        />
      </div>

      {active && (
        <div style={{ marginTop: "0" }}>
          <SelectModeDropdown
            org={org}
            modes={availableModes}
            selectedMode={selectedMode}
            jwtToken={jwtToken}
            setActive={setActive}
          />
        </div>
      )}
    </div>
  )
}

const SelectModeDropdown = ({ org, modes, selectedMode, jwtToken, setActive }: { org: { id: String, name: String }; modes: string[]; selectedMode: string; jwtToken: string; setActive: (active: Boolean) => void }) => {
  const refresh = useApiTrigger();
  return (
    <div>
      <select
        name="orderBy"
        id="orderBy"
        className={"Widget-dropdown"}
        style={{ width: "100%", marginLeft: "0rem" }}
        value={selectedMode}
        onChange={(e) => {
          const newMode = e.target.value;
          setMode(jwtToken, Number(org.id), newMode)
            .then(() => {
              refresh();
              setActive(false);
            })
            .catch((e) => {
              alert(`Failed to set mode for ${org.name}: ${e.message}`);
            });
        }}
      >
        {modes.map((m) => (
          <option value={m}>{m}</option>
        ))}
      </select>
    </div>
  )

}

interface StateToProps {
  jwtToken: string | undefined;
  user: firebase.User | undefined;
  userData: UserData | undefined;
}

interface DispatchFromProps {
  setUser: (_: firebase.User | undefined) => void;
  setUserData: (_: UserData | undefined) => void;
}

interface Props extends StateToProps, DispatchFromProps { }

const mapStateToProps = (state: ApplicationState) => ({
  jwtToken: state.login.jwtToken,
  user: state.login.user,
  userData: state.login.userData,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      setUser: setUserAction,
      setUserData: setUserDataAction,
    },
    dispatch
  );

export default connect<StateToProps, DispatchFromProps>(
  // @ts-ignore
  mapStateToProps,
  mapDispatchToProps
)(ModePage);
